import moment from 'moment';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import isNumber from 'lodash/isNumber';

import { pipe } from '../../../../../utils/pipe';
import { dataSetsProps } from '../consts/graphSettings';
import { COMPLETE_DATE_FORMAT } from '../../../constants';
import { PERIOD } from '../../../../common/Charts/constants';
import { TIMELINE } from '../../../../common/Charts/options/timeline';
import { COLORS, FONT_FAMILY } from '../../../../../constants/styles';

const calculatePeriod = ({ from, to }) => {
  return Math.abs(to.diff(from, 'days'));
};

const getSafeValue = value =>
  value &&
  Number(
    value
      .replace('<', '')
      .replace('>', '')
      .replace('(', '')
      .replace(')', ''),
  );

const makeDataForGraph = graphData => {
  const {
    rawNormalRange,
    yMin: minYAxisValue,
    yMax: maxYAxisValue,
  } = getNormalRange(graphData);

  return graphData.reduce(
    (a, c) => ({
      ...a,
      datasetsObjects: [
        ...get(a, 'datasetsObjects', {}),
        {
          value: getSafeValue(c.componentResultValue),
          date: moment(c.specimenCollectionDate),
          valueUnit: c.componentResultUnits || '',
          abnormalStatus: c.componentAbnormalStatus || '',
        },
      ],
    }),
    {
      maxYAxisValue,
      minYAxisValue,
      rawNormalRange,
    },
  );
};

const sortDataSets = (dataSets, selector = 'date') =>
  sortBy(dataSets, ds => get(ds, selector));

const sortGraphData = graphData => {
  const datasets = get(graphData, 'datasetsObjects', {});

  return {
    ...graphData,
    datasetsObjects: sortDataSets(datasets),
  };
};

const setColorDependOnCtx = ({
  ctx,
  yMin,
  yMax,
  rawNormalRange,
  normalColor = COLORS.black,
  lowAbnormalColor = COLORS.peach,
  highAbnormalColor = COLORS.orangePink,
}) => {
  const index = ctx.dataIndex;
  const value = ctx.dataset.data[index].y;

  const isLessThan = rawNormalRange && rawNormalRange.includes('<');
  const isGreaterThan = rawNormalRange && rawNormalRange.includes('>');
  const isLessOrEqual = rawNormalRange && rawNormalRange.includes('<OR=');
  const isGreaterOrEqual = rawNormalRange && rawNormalRange.includes('>OR=');

  if (isGreaterOrEqual && isGreaterThan) {
    return value >= yMin ? normalColor : lowAbnormalColor;
  }

  if (isGreaterThan && !isGreaterOrEqual) {
    return value > yMin ? normalColor : lowAbnormalColor;
  }

  if (isLessOrEqual && isLessThan) {
    return value <= yMax ? normalColor : highAbnormalColor;
  }

  if (isLessThan && !isLessOrEqual) {
    return value < yMax ? normalColor : highAbnormalColor;
  }

  if (yMin || yMax) {
    if (value < yMin) return lowAbnormalColor;
    if (value > yMax) return highAbnormalColor;
  }

  return normalColor;
};

const makeDataSets = graphData => {
  const collectedData = get(graphData, 'datasetsObjects', []);
  const rawNormalRange = get(graphData, 'rawNormalRange', '');
  const yMax = get(graphData, 'maxYAxisValue', 0);
  const yMin = get(graphData, 'minYAxisValue', 0);

  const { datasets } = collectedData.reduce(
    (a, c) => ({
      datasets: [
        {
          data: [
            ...a.datasets[0].data,
            { y: get(c, 'value'), x: get(c, 'date') },
          ],
          pointBackgroundColor: ctx =>
            setColorDependOnCtx({ ctx, yMin, yMax, rawNormalRange }),
          pointBorderColor: ctx =>
            setColorDependOnCtx({ ctx, yMin, yMax, rawNormalRange }),
          pointHoverBackgroundColor: COLORS.white,
          pointHoverBorderColor: ctx =>
            setColorDependOnCtx({
              ctx,
              yMin,
              yMax,
              normalColor: COLORS.black,
              lowAbnormalColor: COLORS.peach,
              highAbnormalColor: COLORS.orangePink,
            }),
          pointHoverBorderWidth: 1,
          pointHoverRadius: 4,
          ...dataSetsProps,
        },
      ],
    }),
    { datasets: [{ data: [] }] },
  );

  return {
    ...graphData,
    datasets,
  };
};

const setGraphData = localData => makeDataForGraph => sortData => makeDataSets =>
  pipe(
    makeDataForGraph,
    sortData,
    makeDataSets,
  )(localData);

const getMinMaxYAxis = (input, selector = 'componentResultValue') =>
  input.length &&
  input.reduce(
    (a, c) => ({
      yMin:
        getSafeValue(get(c, selector)) < a.yMin
          ? getSafeValue(get(c, selector))
          : a.yMin,
      yMax:
        getSafeValue(get(c, selector)) > a.yMax
          ? getSafeValue(get(c, selector))
          : a.yMax,
    }),
    { yMin: Number.POSITIVE_INFINITY, yMax: Number.NEGATIVE_INFINITY },
  );

const getNormalRange = (data, selector = '[0].componentResultNormalRange') => {
  const normalRange = get(data, selector, '');
  const { yMin: minYAxisValue, yMax: maxYAxisValue } = getMinMaxYAxis(data);

  if (normalRange) {
    if (normalRange.includes('<')) {
      return {
        yMax: Number(
          normalRange
            .replace('<', '')
            .replace('=', '')
            .replace('OR', '')
            .replace('(', '')
            .replace(')', ''),
        ),
        yMin: minYAxisValue,
        rawNormalRange: normalRange,
      };
    }

    if (normalRange.includes('>')) {
      return {
        yMin: Number(
          normalRange
            .replace('>', '')
            .replace('=', '')
            .replace('OR', '')
            .replace('(', '')
            .replace(')', ''),
        ),
        yMax: maxYAxisValue,
        rawNormalRange: normalRange,
      };
    }
  }

  return (
    (normalRange &&
      normalRange
        .replace('(', '')
        .replace(')', '')
        .replaceAll(',', '.')
        .split('-')
        .sort((a, b) => a - b)
        .reduce(
          (a, c, i) =>
            i === 0 ? { ...a, yMin: Number(c) } : { ...a, yMax: Number(c) },
          {
            yMin: 0,
            yMax: 0,
            rawNormalRange: normalRange,
          },
        )) || { yMin: 0, ymax: 0, rawNormalRange: normalRange }
  );
};

export const shouldShowRange = graphData => {
  const { yMin, yMax } = getNormalRange(graphData);

  return isNumber(yMin) && isNumber(yMax) && yMax !== 0;
};

export const shouldShowGraph = graphData =>
  graphData && graphData.length === 0
    ? true
    : graphData
        .map(gd => getSafeValue(gd.componentResultValue))
        .find(crv => !!crv);

const getDateRangeFromGraphData = graphData =>
  sortBy(
    graphData.map(gd => gd.specimenCollectionDate).filter(scd => !!scd),
  ).reduce(
    (a, c) => ({
      ...a,
      from: moment(c).isBefore(moment(a.from)) ? moment(c) : moment(a.from),
      to: moment(c).isAfter(moment(a.to)) ? moment(c) : moment(a.to),
    }),
    {
      from: moment(new Date(8640000000000000)),
      to: moment(new Date(-8640000000000000)),
    },
  );

export const setOptions = graphData => {
  const { yMin, yMax } = getNormalRange(graphData);
  const { from, to } = getDateRangeFromGraphData(graphData);
  const days = calculatePeriod({ from, to });
  const period = (periodDays => {
    if (periodDays <= 1) {
      return PERIOD.DAY;
    }

    if (periodDays <= 30) {
      return PERIOD.MONTH;
    }

    if (periodDays <= 120) {
      return PERIOD.QUARTER;
    }

    if (periodDays <= 120) {
      return PERIOD.MONTH;
    }

    if (periodDays <= 365) {
      return PERIOD.YEAR;
    }

    return PERIOD.HIGH;
  })(days);

  const timeConfig = get(TIMELINE, period);

  return {
    legend: {
      display: false,
    },
    annotation:
      yMax !== 0 && yMax !== undefined
        ? {
            annotations: [
              {
                yMin,
                yMax,
                type: 'box',
                borderWidth: 0,
                borderColor: COLORS.lightTurquoise,
                yScaleID: 'y-axis-0',
                drawTime: 'beforeDatasetsDraw',
                backgroundColor: COLORS.lightTurquoise,
              },
            ],
          }
        : null,
    scales: {
      xAxes: [
        {
          ...get(timeConfig, 'scales.xAxes[0]', {}),
          ...([PERIOD.YEAR, PERIOD.QUARTER].includes(period) && {
            type: 'time',
            bounds: 'ticks',
            time: {
              ...get(timeConfig, 'scales.xAxes[0].time', {}),
              round: 'day',
            },
          }),
          ...(PERIOD.DAY === period &&
            graphData.length === 1 && {
              time: {
                ...get(timeConfig, 'scales.xAxes[0].time', {}),
                unit: 'minute',
              },
            }),
          ticks: {
            padding: 5,
            stepSize: 1,
            fontSize: 13,
            maxTicksLimit: 7,
            fontColor: COLORS.bluePrimary,
            fontFamily: FONT_FAMILY.primary,
            callback: (tick, i, m) => {
              if (
                i === m.length - 1 &&
                [PERIOD.YEAR, PERIOD.QUARTER].includes(period)
              )
                return '';
              return tick.toUpperCase();
            },
          },
          gridLines: {
            display: false,
          },
          offset: true,
        },
      ],
      yAxes: [
        {
          gridLines: {
            borderDash: [1, 2],
            drawBorder: false,
          },
          position: 'right',
          ticks: {
            fontColor: COLORS.cyanBluishGray,
            fontFamily: FONT_FAMILY.primary,
          },
        },
      ],
    },
    tooltips: {
      displayColors: false,
      bodyFontColor: COLORS.black,
      titleFontStyle: 'bold',
      titleFontColor: COLORS.black,
      backgroundColor: COLORS.white,
      callbacks: {
        label(tooltipItem, data) {
          return data.datasetsObjects[tooltipItem.index].date.format(
            COMPLETE_DATE_FORMAT,
          );
        },
        labelTextColor: () => {
          return `rgba(${COLORS.grayDark}, .75)`;
        },
        title(tooltipItem, data) {
          const { value } = data.datasetsObjects[tooltipItem[0].index];
          const { valueUnit } = data.datasetsObjects[tooltipItem[0].index];
          const abnormality =
            data.datasetsObjects[tooltipItem[0].index].abnormalStatus;
          const surroundedAbnormality = abnormality ? `(${abnormality})` : '';
          return `${value} ${valueUnit} ${surroundedAbnormality}`;
        },
      },
    },
  };
};

export const setConfig = ({ localData, shouldRender = false }) =>
  shouldRender
    ? {
        data: setGraphData(localData)(makeDataForGraph)(sortGraphData)(
          makeDataSets,
        ),
        options: setOptions(localData),
      }
    : null;



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/PatientPortal/LabTestHistoryModal/utils/graphData.js